'use client';

/**
 * Library detail — `/library/{slug}` — editorialized category page.
 *
 * Layout mirrors the design handoff prototype verbatim:
 *   1. Banner  — hue-tinted gradient (cover photo overlay if set), breadcrumbs,
 *                giant serif title, serif blurb, stats strip, CTAs.
 *   2. Chips   — subtopic tags derived from in-library prompts.
 *   3. Feature — Editor's pick, 1.1fr/1fr media+body card.
 *   4. Grid    — "The collection" sort toggle + PromptGrid.
 *   5. Experts — top contributors.
 *
 * Key classes live in globals.css under `.lib-*` — ported from the design CSS.
 */

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { api } from '@/lib/api';
import { LibraryDetail, LibrarySort, Prompt, User } from '@/types';
import PromptGrid from '@/components/prompt/PromptGrid';
import { useAuth } from '@/lib/auth-context';
import { useT } from '@/lib/i18n';

export default function LibraryPage() {
  const router = useRouter();
  const { slug } = useParams<{ slug: string }>();
  const { user: viewer } = useAuth();
  const t = useT();
  const SORTS: { label: string; value: LibrarySort }[] = [
    { label: t('library.sort_top'),          value: 'top_rated' },
    { label: t('library.sort_new'),          value: 'newest' },
    { label: t('library.sort_most_remixed'), value: 'most_remixed' },
  ];

  const [lib, setLib] = useState<LibraryDetail | null>(null);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [experts, setExperts] = useState<User[]>([]);
  const [sort, setSort] = useState<LibrarySort>('top_rated');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    Promise.all([
      api.libraries.get(slug),
      api.libraries.prompts(slug, { sort, size: 24 }),
      api.libraries.experts(slug, 6),
    ])
      .then(([libRes, promptRes, exRes]) => {
        setLib(libRes); setPrompts(promptRes.items); setExperts(exRes); setError('');
      })
      .catch(() => setError(t('library.not_found_msg')))
      .finally(() => setLoading(false));
  }, [slug, sort]);

  // Top tags across the library's prompts → subtopic chips.
  const subtopics = useMemo(() => {
    const counts = new Map<string, number>();
    prompts.forEach((p) => (p.tags || []).forEach((t) => counts.set(t, (counts.get(t) ?? 0) + 1)));
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [prompts]);

  const editorsPick = prompts[0];
  const remaining = prompts
    .slice(editorsPick ? 1 : 0)
    .filter((p) => !activeTag || p.tags?.includes(activeTag));

  if (error) {
    return (
      <div style={{ padding: 80, textAlign: 'center' }}>
        <span className="material-symbols-outlined" style={{ fontSize: 56, color: 'var(--ink-4)' }}>
          library_books
        </span>
        <h1 className="lib-title" style={{ fontSize: 36, margin: '16px 0 8px' }}>
          {t('library.not_found_title')}
        </h1>
        <p style={{ color: 'var(--ink-3)', marginBottom: 20 }}>{error}</p>
        <Link href="/explore" className="ps-btn ps-btn-secondary no-underline">
          <span className="material-symbols-outlined ps-icon">arrow_back</span>
          {t('library.browse_libraries')}
        </Link>
      </div>
    );
  }

  if (!lib && loading) {
    return <div style={{ padding: 80, display: 'grid', placeItems: 'center' }}><div className="spinner" /></div>;
  }
  if (!lib) return null;

  const hue = lib.hue ?? '260';
  const hasCover = !!lib.cover_image_url;

  // Banner background: cover image overlay if set, otherwise layered radial
  // gradients tinted with the library's hue. Dark-mode override lives in the
  // CSS as a fallback so the banner stays rich even without a cover.
  const bannerBg: React.CSSProperties = hasCover
    ? {
        backgroundImage: `linear-gradient(135deg, oklch(0.22 0.1 ${hue} / 0.85), oklch(0.12 0.07 ${hue} / 0.94)), url("${lib.cover_image_url}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : {
        background:
          `radial-gradient(circle at 18% 22%, oklch(0.90 0.11 ${hue}), transparent 55%), ` +
          `radial-gradient(circle at 82% 78%, oklch(0.88 0.14 ${hue}), transparent 55%), ` +
          `linear-gradient(135deg, oklch(0.96 0.05 ${hue}), oklch(0.99 0.02 ${hue}))`,
      };

  // Dark-mode hue-tinted fallback, used when no cover is set. Variables feed
  // into the CSS rule in globals.css via the inline `--lib-dark-bg` custom
  // property on the banner element.
  const darkBg = `linear-gradient(135deg, oklch(0.24 0.09 ${hue}), oklch(0.16 0.05 ${hue}))`;

  return (
    <div className="lib-screen">
      {/* ── Banner ─────────────────────────────────────────── */}
      <div
        className="lib-banner"
        data-has-cover={hasCover ? 'true' : 'false'}
        style={{
          ...bannerBg,
          ['--lib-dark-bg' as string]: darkBg,
        }}
      >
        <div className="lib-banner-inner">
          {/* Breadcrumbs */}
          <div className="lib-crumbs">
            <Link href="/">{t('library.breadcrumb_home')}</Link>
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
            <Link href="/explore">{t('library.breadcrumb_libraries')}</Link>
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
            <span className="lib-crumb-current">{lib.name}</span>
          </div>

          <h1 className="lib-title">{lib.name}.</h1>

          {lib.description && <p className="lib-blurb">{lib.description}</p>}

          <div className="lib-banner-stats">
            <div>
              <strong>{lib.prompt_count.toLocaleString()}</strong>
              {t('library.stat_prompts')}
            </div>
            <div>
              <strong>{lib.contributor_count.toLocaleString()}</strong>
              {t('library.stat_contributors')}
            </div>
            <div>
              <strong>{lib.saves_this_month.toLocaleString()}</strong>
              {t('library.stat_saves_month')}
            </div>
          </div>

          <div className="lib-banner-actions">
            <button
              type="button"
              className="ps-btn ps-btn-primary"
              onClick={() => {
                const first = experts[0];
                if (first) router.push(`/profile/${first.username}`);
              }}
            >
              <span className="material-symbols-outlined ps-icon">bookmark_add</span>
              {t('library.follow_library')}
            </button>
            <Link
              href={viewer ? `/create?category=${encodeURIComponent(lib.prompt_category)}` : '/auth/login'}
              className="ps-btn ps-btn-secondary no-underline"
            >
              <span className="material-symbols-outlined ps-icon">add</span>
              {t('library.share_a_prompt', { name: lib.name.toLowerCase() })}
            </Link>
          </div>
        </div>
      </div>

      {/* ── Subtopic chips ─────────────────────────────────── */}
      {subtopics.length > 0 && (
        <div className="chip-row" style={{ paddingTop: 24 }}>
          <button
            className="chip"
            aria-pressed={activeTag === null}
            onClick={() => setActiveTag(null)}
          >
            {t('library.all_topics')}
          </button>
          {subtopics.map(([tag, count]) => (
            <button
              key={tag}
              className="chip"
              aria-pressed={activeTag === tag}
              onClick={() => setActiveTag(activeTag === tag ? null : tag)}
            >
              #{tag}
              <span style={{ color: 'var(--ink-3)', marginLeft: 4, fontSize: 11 }}>{count}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Editor's pick feature card ─────────────────────── */}
      {editorsPick && (
        <div
          className="lib-feature"
          onClick={() => router.push(`/prompt/${editorsPick.id}`)}
        >
          <div
            className="lib-feature-media"
            style={{
              background: hasCover ? 'var(--bg-soft)' : `oklch(0.94 0.04 ${hue})`,
            }}
          >
            {editorsPick.cover_image_url ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={editorsPick.cover_image_url} alt="" />
            ) : (
              <div
                className="lib-feature-prompt"
                style={{ background: `linear-gradient(180deg, oklch(0.96 0.03 ${hue}), transparent)` }}
              >
                {editorsPick.content?.slice(0, 480)}
              </div>
            )}
          </div>
          <div className="lib-feature-body">
            <div className="lib-feature-eyebrow" style={{ color: `oklch(0.5 0.16 ${hue})` }}>
              {t('library.editors_pick')}
            </div>
            <h2 className="lib-feature-title">{editorsPick.title}</h2>
            {editorsPick.description && (
              <p className="lib-feature-desc">{editorsPick.description}</p>
            )}
            {editorsPick.user && (
              <div className="lib-feature-meta">
                <PersonAvatar user={editorsPick.user} size={36} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="lib-feature-meta-name">{editorsPick.user.name}</div>
                  <div className="lib-feature-meta-sub">
                    @{editorsPick.user.username}
                    {typeof (editorsPick as Prompt & { save_count?: number }).save_count === 'number' &&
                      ` · ${(editorsPick as Prompt & { save_count?: number }).save_count} ${t('library.saved_suffix')}`}
                  </div>
                </div>
                {editorsPick.category && (
                  <span className="ps-model-badge" style={{ position: 'static' }}>
                    {editorsPick.category}
                  </span>
                )}
              </div>
            )}
            <div style={{ marginTop: 20 }}>
              <span className="ps-btn ps-btn-primary">
                {t('library.open_prompt')}
                <span className="material-symbols-outlined ps-icon">arrow_outward</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── "The collection" ───────────────────────────────── */}
      <div className="section-hd">
        <div>
          <div className="eyebrow">{t('library.more_in', { name: lib.name })}</div>
          <h2>{t('library.the_collection')}</h2>
        </div>
        <div className="seg" role="tablist" aria-label="Sort">
          {SORTS.map((s) => (
            <button key={s.value} aria-pressed={sort === s.value} onClick={() => setSort(s.value)}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid" data-layout="masonry">
        {remaining.length > 0 ? (
          <PromptGrid prompts={remaining} loading={loading} />
        ) : (
          <div style={{ gridColumn: '1 / -1', padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>
            {activeTag ? t('library.empty_tag') : t('library.empty_more')}
          </div>
        )}
      </div>

      {/* ── Experts ─────────────────────────────────────────── */}
      {experts.length > 0 && (
        <section className="lib-experts">
          <div className="section-hd" style={{ padding: 0 }}>
            <div>
              <div className="eyebrow">{t('library.section_people')}</div>
              <h2>{t('library.top_contributors')}</h2>
            </div>
          </div>
          <div className="lib-experts-grid">
            {experts.map((u) => (
              <Link key={u.id} href={`/profile/${u.username}`} className="lib-expert">
                {u.avatar_url ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img className="lib-expert-avatar" src={u.avatar_url} alt={u.name} />
                ) : (
                  <div className="lib-expert-avatar-initial">
                    {u.name.slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="lib-expert-name">{u.name}</div>
                <div className="lib-expert-sub">
                  @{u.username}
                  {typeof u.followers_count === 'number' && ` · ${u.followers_count} ${t('library.followers_suffix')}`}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/* ── Small helpers ────────────────────────────────────────── */
function PersonAvatar({ user, size = 40 }: { user: { avatar_url?: string; name: string }; size?: number }) {
  if (user.avatar_url) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={user.avatar_url} alt={user.name}
        style={{
          width: size, height: size, borderRadius: '50%', objectFit: 'cover',
          border: '1px solid var(--line)', flex: 'none',
        }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flex: 'none',
      background: 'linear-gradient(135deg, var(--accent), var(--accent-ink))',
      display: 'grid', placeItems: 'center',
      color: 'white', fontFamily: 'var(--font-serif)', fontWeight: 500,
      fontSize: size * 0.4,
      border: '1px solid var(--line)',
    }}>
      {user.name.slice(0, 1).toUpperCase()}
    </div>
  );
}
